'use client';

import { useTranslations } from 'next-intl';
import { PublicConsentField } from './PublicConsentField';
import { FreeTryNote } from '@/contexts/subscriptions/components/FreeTryNote';
import { AllowanceLeftNote } from '@/contexts/subscriptions/components/AllowanceLeftNote';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Shield, Heart, Sparkles, AlertTriangle } from 'lucide-react';
import type { WizardScenario } from '../../types/enums';
import type { ContentParameters } from './ParametersStep';
import type { VoiceSettings } from './VoiceStep';

import type { AudioOptions, IllustrationOptions } from './OutputStep';
import { clampPageCount, minutesForBlocks } from '@/contexts/stories/utils/storyShape';

interface ReviewStepProps {
  scenario: WizardScenario;
  parameters: ContentParameters;
  voiceSettings?: VoiceSettings;
  sourceStoryTitle?: string | null;
  wantAudio?: boolean;
  wantIllustrations?: boolean;
  illustrationStyle?: string;
  illustrationFormat?: string;
  audioOptions?: AudioOptions;
  illustrationOptions?: IllustrationOptions;
  characterNames?: Record<string, string>;
  /** Actual block count from existing story (overrides duration-based estimate) */
  sourceStoryBlockCount?: number;
  /** Actual total characters from existing story (exact audio forecast) */
  sourceStoryTotalChars?: number;
  /** Public-publishing consent — shown here (last step) as the final opt-in. */
  publicConsent?: boolean;
  onPublicConsentChange?: (checked: boolean) => void;
  /**
   * SC-07 only: what the SOURCE story has, and whether the parent asked for the
   * text alone. A translation reproduces the original's layers unless told not
   * to, and this screen used to answer "Only the story" while the executor was
   * about to order a book and a narration.
   */
  translationHasBook?: boolean;
  translationHasNarration?: boolean;
  translationTextOnly?: boolean;
}

const ROLE_ICONS: Record<string, React.ReactNode> = {
  hero: <Shield className="h-3 w-3" />,
  friend: <Heart className="h-3 w-3" />,
  helper: <Sparkles className="h-3 w-3" />,
  obstacle: <AlertTriangle className="h-3 w-3" />,
};

export function ReviewStep({ scenario, parameters, voiceSettings, sourceStoryTitle, wantAudio, wantIllustrations, illustrationStyle, illustrationFormat, audioOptions, illustrationOptions, characterNames, sourceStoryBlockCount, sourceStoryTotalChars, publicConsent, onPublicConsentChange, translationHasBook, translationHasNarration, translationTextOnly }: ReviewStepProps) {
  const t = useTranslations('content.wizard');

  const isFromExisting = (['SC-05', 'SC-06', 'SC-07', 'SC-11'] as WizardScenario[]).includes(scenario);

  // Authoritative from-text signal = the creation mode itself (what buildCreateParams
  // uses), NOT the scenario. from-text-full resolves to SC-04's DAG, which is NOT in
  // the SC-08/09/10 list — reading the scenario mislabels it as "Генерация ИИ" and
  // hides the basis/change fields.
  const isFromText = parameters.creationMode === 'from-text' && !isFromExisting;

  // A translation does not pick outputs on an output step: it inherits the
  // source story's layers (unless the parent ticked "the text only"), so the
  // line names what the follow-up will actually order.
  const translationLayers =
    scenario === 'SC-07' && !translationTextOnly && (translationHasBook || translationHasNarration)
      ? translationHasBook && translationHasNarration
        ? 'review.outputTranslateFull'
        : translationHasBook
          ? 'review.outputTranslateBook'
          : 'review.outputTranslateAudio'
      : null;

  // Determine output type label — for extend scenarios, story already exists
  const outputTypeKey = translationLayers ?? (isFromExisting
    ? (wantAudio && wantIllustrations
        ? 'review.outputAudioBook'
        : wantAudio
          ? 'review.outputAudioOnly'
          : wantIllustrations
            ? 'review.outputBookOnly'
            : 'review.outputStoryOnly')
    : (wantAudio && wantIllustrations
        ? 'review.outputFull'
        : wantAudio
          ? 'review.outputStoryAudio'
          : wantIllustrations
            ? 'review.outputStoryBook'
            : 'review.outputStoryOnly'));

  // Determine creation mode label
  const creationModeKey = isFromText
    ? 'review.creationModeFromText'
    : isFromExisting
      ? 'review.creationModeFromText'
      : 'review.creationModeGenerate';

  const fields = [
    { label: t('review.scenario'), value: t(`scenarios.${scenario}.title`) },
    { label: t('review.outputType'), value: t(outputTypeKey) },
    // creationMode is about how the STORY comes to be — meaningless (and
    // confusing) when only audio/book are added to an existing story.
    ...(isFromExisting ? [] : [{ label: t('review.creationMode'), value: t(creationModeKey) }]),
  ];
  const hasAudioAI = (['SC-02', 'SC-04'] as WizardScenario[]).includes(scenario);

  if (isFromExisting && sourceStoryTitle) {
    fields.push({ label: t('storySelect.selected'), value: sourceStoryTitle });
  } else if (isFromText) {
    fields.push({ label: t('review.title'), value: parameters.title ?? '' });
    // basis-seeded (based_on): the source text loads server-side, so it isn't in
    // params — show the basis + the transformation the user asked for instead of a
    // blank text field.
    if (parameters.basisStoryId) {
      fields.push({ label: t('storyInput.startFromBasis'), value: parameters.title ?? '' });
    } else if (parameters.text) {
      fields.push({
        label: t('review.text'),
        value: `${parameters.text.substring(0, 200)}${parameters.text.length > 200 ? '…' : ''}`,
      });
    }
    if (parameters.fromTextChangeVector?.trim()) {
      fields.push({ label: t('storyInput.fromTextChangeVector'), value: parameters.fromTextChangeVector.trim() });
    }
    const swaps = (parameters.fromTextReplacements ?? []).filter((r) => r.from?.trim() && r.to?.trim());
    if (swaps.length) {
      fields.push({
        label: t('storyInput.basisCastReplace'),
        value: swaps.map((r) => `${r.from} → ${r.to}`).join(', '),
      });
    }
  } else if (!isFromExisting) {
    // A layer added to an existing story has no prompt of its own: an empty «Описание» row
    // read as a missing setting (dev run 4, the comic layer's review).
    fields.push({ label: t('review.prompt'), value: parameters.prompt });
  }

  // Extending an EXISTING story: language/age/genre belong to the story that
  // already exists — showing them reads as "we will generate a story again".
  // The summary must list only what will actually be produced.
  if (!isFromExisting) {
    fields.push(
      { label: t('review.language'), value: t(`languages.${parameters.language}`) },
    );

    if (!isFromText) {
      fields.push({ label: t('review.genre'), value: t(`genres.${parameters.genre}`) });
    }

    fields.push(
      { label: t('review.ageGroup'), value: t(`ageGroups.${parameters.ageGroup}`) },
    );
  }

  if (!isFromText && !isFromExisting) {
    if (parameters.theme) {
      fields.push({ label: t('review.theme'), value: t(`themes.${parameters.theme}`) });
    }
    if (parameters.setting) {
      fields.push({ label: t('review.setting'), value: t(`settings.${parameters.setting}`) });
    }
    if (parameters.energy) {
      fields.push({ label: t('review.energy'), value: t(`energyLevels.${parameters.energy}`) });
    }
    // Length is stated the way it was chosen — in pages, with the reading time
    // after it. It used to appear only for orders with narration, which is the
    // one case where the number was NOT the page count of the book.
    if (parameters.pageCount) {
      const pages = clampPageCount(parameters.pageCount, parameters.ageGroup);
      fields.push({
        label: t('review.length'),
        value: t('parameters.lengthHint', {
          pages,
          minutes: minutesForBlocks(pages, parameters.ageGroup, parameters.language),
        }),
      });
    } else if (hasAudioAI && parameters.desiredDurationMinutes) {
      fields.push({
        label: t('review.desiredDuration'),
        value: t('parameters.durationValue', { minutes: parameters.desiredDurationMinutes }),
      });
    }
  }

  if (wantIllustrations && illustrationStyle) {
    fields.push({ label: t('review.bookStyle'), value: t(`output.bookStyles.${illustrationStyle}`) });
  }
  if (wantIllustrations && illustrationFormat) {
    fields.push({ label: t('output.bookFormat'), value: t(`output.bookFormats.${illustrationFormat}`) });
  }

  if (wantAudio && audioOptions) {
    const audioFlags: string[] = [];
    if (audioOptions.autoGenerateBlocks) audioFlags.push(t('review.audioAutoGenerate'));
    if (audioOptions.autoAssemble) audioFlags.push(t('review.audioAutoAssemble'));
    if (audioFlags.length > 0) {
      fields.push({ label: t('review.audioAutomation'), value: audioFlags.join(', ') });
    }
  }

  if (wantIllustrations && illustrationOptions) {
    const bookFlags: string[] = [];
    if (illustrationOptions.autoGeneratePortraits) bookFlags.push(t('review.bookAutoPortraits'));
    if (illustrationOptions.autoGenerateImages) bookFlags.push(t('review.bookAutoImages'));
    if (bookFlags.length > 0) {
      fields.push({ label: t('review.bookAutomation'), value: bookFlags.join(', ') });
    }
  }

  if (voiceSettings) {
    const voiceLabel = voiceSettings.voiceName === 'female'
      ? t('voice.presetFemale')
      : voiceSettings.voiceName === 'male'
        ? t('voice.presetMale')
        : voiceSettings.voiceName || voiceSettings.voiceId;
    fields.push({ label: t('review.voice'), value: voiceLabel });
  }

  return (
    <div data-testid="content-wizard-review-step" className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('review.heading')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-2 sm:gap-y-3">
            {fields.map((field) => (
              <div key={field.label} className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {field.label}
                </span>
                <span className="text-sm text-foreground break-words">
                  {field.value}
                </span>
              </div>
            ))}

            {/* Characters — full width. Only relevant to illustrations; an
                audio-only run neither uses nor creates characters, so hide it
                there (was showing a misleading "characters will be auto-created"). */}
            {wantIllustrations && (
            <div className="sm:col-span-2 flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('review.characters')}
              </span>
              {parameters.characterAssignments.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {parameters.characterAssignments.map((a) => (
                    <Badge key={a.characterId} variant="outline" className="gap-1 text-xs break-all">
                      {ROLE_ICONS[a.role]}
                      {characterNames?.[a.characterId] || a.characterId.slice(0, 8)}
                      <span className="text-muted-foreground">
                        ({t(`characters.roles.${a.role}`)})
                      </span>
                    </Badge>
                  ))}
                </div>
              ) : (parameters.characterIds.length > 0 || (parameters.pendingPhotoHeroes?.length ?? 0) > 0) ? (
                /* Picked, but given no ROLE — which is the normal case for a
                   pasted text, where the roles are the author's. The screen used
                   to read "no characters, they will be created automatically",
                   which was false: these heroes travel with the story and their
                   portraits are reused. */
                <div className="flex flex-wrap gap-2">
                  {parameters.characterIds.map((id) => (
                    <Badge key={id} variant="outline" className="gap-1 text-xs break-all">
                      {parameters.photoHeroes?.some((h) => h.characterId === id) ? '📷 ' : ''}
                      {characterNames?.[id] || parameters.photoHeroes?.find((h) => h.characterId === id)?.name || id.slice(0, 8)}
                    </Badge>
                  ))}
                  {/* A hero from a PHOTO is still a draft here — the character is
                      built at creation — but it is the one the parent cares about
                      most, and the screen used to answer "none". */}
                  {(parameters.pendingPhotoHeroes ?? []).map((h) => (
                    <Badge key={h.photoKey} variant="outline" className="gap-1 text-xs break-all">
                      📷 {h.name.trim() || h.photoKey.slice(0, 8)}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-foreground">
                  {t('characters.none')}
                </span>
              )}
            </div>
            )}
          </div>
        </CardContent>
      </Card>

      {onPublicConsentChange && (
        <PublicConsentField checked={publicConsent} onChange={onPublicConsentChange} />
      )}

      <p className="text-sm text-muted-foreground">{t('review.confirm')}</p>
      {/* Set expectations before the paid click: the package (text → audio ∥
          book) is minutes, not seconds, and finishes without the tab open. */}
      <div className="space-y-1 rounded-md border bg-muted/40 px-3 py-2">
        <p className="text-xs text-muted-foreground" data-testid="content-wizard-duration-note">
          {t('review.durationNote')}
        </p>
        {/* What is left, and the date on the free try — both before the press,
            not after it (gap 4, FIRST.R20). */}
        <AllowanceLeftNote wantsBook={wantIllustrations} />
        <FreeTryNote />
      </div>
    </div>
  );
}
